import { parseToken } from './parse-token.js'

// Warning appears this long before expiry (kept at the portals' existing 1 min).
export const WARNING_LEAD_MS = 60000

// Decide what a session-check tick should do, using the SHARED cookie token as
// the source of truth rather than a tab's stale in-memory expiry (AB#26125).
//
// Portal tabs share the token cookie, but each tab historically tracked expiry
// from an in-memory snapshot taken at load and never reconciled. That let a
// background tab warn/log-out on a stale clock even though another (active) tab
// had refreshed the shared cookie, and a stale tab's logout would clear the
// shared cookie and take the active tab down with it. Reading the cookie each
// tick fixes both: activity in any tab keeps all tabs alive, and a tab only
// tears down when the shared cookie is genuinely gone/expired.
//
// Pure and side-effect free; `parse` is injectable for testing.
//
// @param {object} args
// @param {string|null} args.cookieToken       raw JWT from the shared cookie
// @param {number} [args.knownExpirySeconds]   this tab's in-memory exp claim
// @param {number} args.nowMs                  Date.now()
// @param {number} [args.warningLeadMs]        lead time for the warning
// @param {(t: string) => object|null} [args.parse]
// @returns {{ kind: 'gone'|'expired'|'warning'|'active', expirySeconds?: number, userId?: number, adopt?: boolean, timeLeftMs?: number }}
export function decideSessionTick ({ cookieToken, knownExpirySeconds, nowMs, warningLeadMs = WARNING_LEAD_MS, parse = parseToken }) {
	if (!cookieToken) return { kind: 'gone' }

	let parsed
	try {
		parsed = parse(cookieToken)
	} catch {
		return { kind: 'gone' }
	}
	if (!parsed || typeof parsed.exp !== 'number') return { kind: 'gone' }

	const expirySeconds = parsed.exp
	const timeLeftMs = expirySeconds * 1000 - nowMs
	// A cookie expiry different from what this tab holds means another tab
	// refreshed (or replaced) the shared token; adopt it.
	const adopt = expirySeconds !== knownExpirySeconds
	const base = { expirySeconds, userId: parsed.userId, adopt, timeLeftMs }

	if (timeLeftMs <= 0) return { kind: 'expired', ...base }
	if (timeLeftMs <= warningLeadMs) return { kind: 'warning', ...base }
	return { kind: 'active', ...base }
}
